<template>
  <el-dialog
    :title="isEdit ? '编辑商品' : '新增商品'"
    :visible.sync="dialogVisible"
    width="600px"
    @close="handleClose"
  >
    <el-form
      ref="productForm"
      :model="formData"
      :rules="rules"
      label-width="120px"
    >
      <!-- 商品编码 -->
      <el-form-item label="商品编码" prop="product_code">
        <el-input
          v-model="formData.product_code"
          placeholder="请输入商品编码（大写字母和数字）"
          :disabled="isEdit"
          maxlength="50"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          必须按照聚水潭的商品编码创建，只能包含大写字母和数字
        </div>
      </el-form-item>

      <!-- 商品名称 -->
      <el-form-item label="商品名称" prop="product_name">
        <el-input
          v-model="formData.product_name"
          placeholder="请输入商品名称"
          maxlength="50"
          show-word-limit
        />
      </el-form-item>

      <!-- 商品价格 -->
      <el-form-item label="商品价格" prop="price">
        <el-input-number
          v-model="formData.price"
          :precision="2"
          :step="1"
          :min="0.01"
          controls-position="right"
          style="width: 100%"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          单位：元
        </div>
      </el-form-item>

      <!-- 商品描述 -->
      <el-form-item label="商品描述" prop="description">
        <el-input
          v-model="formData.description"
          type="textarea"
          :rows="4"
          placeholder="请输入商品描述"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>

      <!-- 收款商户 -->
      <el-form-item label="收款商户" prop="merchant_code">
        <el-select
          v-model="formData.merchant_code"
          placeholder="请选择收款商户（可选）"
          clearable
          filterable
          style="width: 100%"
          @change="handleMerchantChange"
        >
          <el-option
            v-for="item in merchantList"
            :key="item.itemValue"
            :label="item.itemLabel"
            :value="item.itemValue"
          >
            <span>{{ item.itemLabel }}</span>
            <span style="float: right; color: #8492a6; font-size: 12px">
              {{ item.itemValue }}
            </span>
          </el-option>
        </el-select>
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          不选择则使用系统默认商户号收款
        </div>
      </el-form-item>

      <!-- 默认分佣比例（仅新增时显示） -->
      <el-form-item v-if="!isEdit" label="默认分佣比例" prop="commission_rate">
        <el-input-number
          v-model="formData.commission_rate"
          :precision="2"
          :step="1"
          :min="0"
          :max="100"
          controls-position="right"
          style="width: 100%"
        />
        <div style="margin-top: 5px; font-size: 12px; color: #909399">
          单位：%，默认10%，后端固定使用百分比类型
        </div>
      </el-form-item>

      <!-- 商品状态（仅新增时显示） -->
      <el-form-item v-if="!isEdit" label="商品状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio :label="1">上架</el-radio>
          <el-radio :label="0">下架</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-alert
        v-if="isEdit"
        title="提示"
        type="warning"
        :closable="false"
        style="margin-top: 10px"
      >
        <div style="font-size: 12px; line-height: 1.8">
          1. 商品编码创建后不可修改<br>
          2. 分佣配置和状态请使用专用功能修改
        </div>
      </el-alert>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
        {{ isEdit ? '保存' : '创建' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { createProduct, updateProduct } from '@/api/iccid-product'
import { getDictData } from '@/api/dict'

export default {
  name: 'ProductDialog',
  data() {
    const validateProductCode = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入商品编码'))
      } else if (!/^[A-Z0-9]+$/.test(value)) {
        callback(new Error('商品编码只能包含大写字母和数字'))
      } else {
        callback()
      }
    }

    const validatePrice = (rule, value, callback) => {
      if (value === null || value === undefined) {
        callback(new Error('请输入商品价格'))
      } else if (value <= 0) {
        callback(new Error('商品价格必须大于0'))
      } else {
        callback()
      }
    }

    const validateCommissionRate = (rule, value, callback) => {
      if (value === null || value === undefined) {
        callback()  // 可选字段
      } else if (value < 0 || value > 100) {
        callback(new Error('分佣比例范围为0-100'))
      } else {
        callback()
      }
    }

    return {
      dialogVisible: false,
      submitLoading: false,
      isEdit: false,
      merchantList: [],
      formData: {
        id: null,
        product_code: '',
        product_name: '',
        price: null,
        description: '',
        merchant_code: '',
        merchant_name: '',
        commission_rate: 10,
        status: 1
      },
      rules: {
        product_code: [
          { required: true, validator: validateProductCode, trigger: 'blur' }
        ],
        product_name: [
          { required: true, message: '请输入商品名称', trigger: 'blur' },
          { min: 2, max: 50, message: '商品名称长度为2-50个字符', trigger: 'blur' }
        ],
        price: [
          { required: true, validator: validatePrice, trigger: 'blur' }
        ],
        commission_rate: [
          { validator: validateCommissionRate, trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    // 获取商户号列表
    async fetchMerchantList() {
      try {
        const { data } = await getDictData('FYPAY')
        this.merchantList = data || []
      } catch (error) {
        console.error('获取商户号列表失败:', error)
      }
    },

    // 商户选择变更
    handleMerchantChange(value) {
      if (value) {
        const selected = this.merchantList.find(item => item.itemValue === value)
        if (selected) {
          this.formData.merchant_name = selected.itemLabel
        }
      } else {
        // 清空选择
        this.formData.merchant_code = ''
        this.formData.merchant_name = ''
      }
    },

    // 打开弹窗
    async open(data) {
      this.dialogVisible = true
      this.isEdit = !!data

      // 获取商户号列表
      await this.fetchMerchantList()

      if (data) {
        // 编辑模式
        this.formData = {
          id: data.id,
          product_code: data.product_code,
          product_name: data.product_name,
          price: parseFloat(data.price),
          description: data.description || '',
          merchant_code: data.merchant_code || '',
          merchant_name: data.merchant_name || '',
          commission_rate: data.default_commission_value || 10,
          status: data.status
        }
      } else {
        // 新增模式
        this.formData = {
          id: null,
          product_code: '',
          product_name: '',
          price: null,
          description: '',
          merchant_code: '',
          merchant_name: '',
          commission_rate: 10,
          status: 1
        }
      }
    },

    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
      this.$refs.productForm.resetFields()
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.productForm.validate(async valid => {
        if (!valid) return

        this.submitLoading = true
        try {
          if (this.isEdit) {
            // 编辑商品
            await updateProduct({
              id: this.formData.id,
              product_name: this.formData.product_name,
              price: this.formData.price,
              description: this.formData.description,
              merchant_code: this.formData.merchant_code || '',
              merchant_name: this.formData.merchant_name || ''
            })
            this.$message.success('商品更新成功')
          } else {
            // 创建商品
            await createProduct({
              product_code: this.formData.product_code,
              product_name: this.formData.product_name,
              price: this.formData.price,
              description: this.formData.description,
              merchant_code: this.formData.merchant_code || '',
              merchant_name: this.formData.merchant_name || '',
              commission_rate: this.formData.commission_rate,
              status: this.formData.status
            })
            this.$message.success('商品创建成功')
          }

          this.handleClose()
          this.$emit('success')
        } catch (error) {
          this.$message.error(error.message || (this.isEdit ? '更新失败' : '创建失败'))
        } finally {
          this.submitLoading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}
</style>
